import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { supabase } from './supabaseClient';
import { apiFetch, syncAppUserFromBackend } from './utils/api';
import { getProfileDisplayName } from './utils/profile';
import Sidebar from './components/Sidebar';
import LogPage from './pages/LogPage';
import NewEntryPage from './pages/NewEntryPage';
import GoalsPage from './pages/GoalsPage';
import OrbitPage from './pages/OrbitPage';
import LessonsPage from './pages/LessonsPage';
import IdeasPage from './pages/IdeasPage';
import DecisionsPage from './pages/DecisionsPage';
import JourneyPage from './pages/JourneyPage';
import SettingsPage from './pages/SettingsPage';
import PublicProfilePage from './pages/PublicProfilePage';
import ShowcasePage from './pages/ShowcasePage';
import AdminPage from './pages/AdminPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import ContactPage from './pages/ContactPage';
import '@/index.css';
import { DaypopBrand, DaypopLogo } from './components/WaypointLogo';
import { SESSION_ANCHOR_KEY, useSessionTimeout } from './hooks/useSessionTimeout';

/** When profile comes only from Supabase client, attach chapter title for the UI. */
async function mergeCurrentChapterIntoProfile(row) {
  if (!row) return null;
  if (row.current_chapter !== undefined) return row;
  if (!row.current_chapter_id) {
    return { ...row, current_chapter: null };
  }
  const { data: ch, error: chErr } = await supabase
    .from('chapters')
    .select('id,title,status,start_date,theme')
    .eq('id', row.current_chapter_id)
    .maybeSingle();
  if (chErr) {
    console.warn('Error fetching chapter:', chErr);
  }
  return { ...row, current_chapter: ch ?? null };
}

// Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dbUser, setDbUser] = useState(null);

  /** Load app user row; does not toggle global loading (avoids stuck spinner on refetch / OAuth races). */
  const fetchDbUser = useCallback(async (authUserId) => {
    if (!authUserId) return null;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user:', error);
      }
      const row = await mergeCurrentChapterIntoProfile(data ?? null);
      setDbUser(row);
      return row;
    } catch (err) {
      console.error('Error:', err);
      setDbUser(null);
      return null;
    }
  }, []);

  /** Prefer API so server creates public.users (RLS-safe); fall back to direct Supabase read. */
  const loadAppUserProfile = useCallback(
    async (authUserId, accessToken, syncOpts = {}) => {
      const sync = await syncAppUserFromBackend(accessToken, syncOpts);
      if (sync.user) {
        setDbUser(sync.user);
        return sync.user;
      }
      if (sync.error && !sync.skipped) {
        console.warn('[Daypop] Backend profile sync:', sync.error);
      }
      return fetchDbUser(authUserId);
    },
    [fetchDbUser]
  );

  const markBootstrapComplete = useCallback(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const run = async () => {
          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error('[Daypop] getSession:', error);
          }
          const s = data?.session ?? null;
          if (cancelled) return;
          setSession(s);
          setUser(s?.user ?? null);
          if (s?.user) {
            await loadAppUserProfile(s.user.id, s.access_token, { timeoutMs: 12000 });
          } else {
            setDbUser(null);
          }
        };
        // Cap total wait so a hung Supabase/API call cannot leave the app on "Loading..." forever
        // (common in dev with React StrictMode + localhost session while backend is unreachable).
        await Promise.race([
          run(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('auth_bootstrap_timeout')), 15000)
          ),
        ]);
      } catch (e) {
        if (e?.message === 'auth_bootstrap_timeout') {
          console.warn(
            '[Daypop] Auth bootstrap timed out (15s). Releasing loading state — check backend URL and Supabase.'
          );
          // Do not clear session: user may be signed in; profile sync may still finish in the background.
        } else {
          console.error('[Daypop] Auth bootstrap failed:', e);
          if (!cancelled) {
            setSession(null);
            setUser(null);
            setDbUser(null);
          }
        }
      } finally {
        // Always clear loading — StrictMode unmount sets cancelled=true; skipping here left
        // localhost (with a saved session) stuck on /dashboard with a permanent spinner.
        setLoading(false);
      }
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (event === 'SIGNED_IN') {
        sessionStorage.setItem(SESSION_ANCHOR_KEY, String(Date.now()));
      } else if (event === 'SIGNED_OUT' || !s) {
        sessionStorage.removeItem(SESSION_ANCHOR_KEY);
      } else if (event === 'INITIAL_SESSION' && s?.user) {
        if (!sessionStorage.getItem(SESSION_ANCHOR_KEY)) {
          sessionStorage.setItem(SESSION_ANCHOR_KEY, String(Date.now()));
        }
      }
      try {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          await loadAppUserProfile(s.user.id, s.access_token, { timeoutMs: 12000 });
        } else {
          setDbUser(null);
        }
      } catch (e) {
        console.error('[Daypop] onAuthStateChange:', e);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadAppUserProfile]);

  const refreshUser = useCallback(async () => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (!s?.user) return null;
    const sync = await syncAppUserFromBackend(s.access_token);
    if (sync.user) {
      setDbUser(sync.user);
      return sync.user;
    }
    return fetchDbUser(s.user.id);
  }, [fetchDbUser]);

  const value = {
    user,
    session,
    dbUser,
    loading,
    setDbUser,
    refreshUser,
    markBootstrapComplete,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** Must render under BrowserRouter + AuthProvider; enforces idle + max session length. */
function SessionTimeoutWatcher() {
  const { session } = useAuth();
  useSessionTimeout(session);
  return null;
}

function oauthErrorMessage(raw) {
  let s = typeof raw === 'string' ? raw : raw?.message || '';
  if (!s && raw && typeof raw === 'object') s = JSON.stringify(raw);
  try {
    const j = JSON.parse(s);
    if (typeof j.msg === 'string') s = j.msg;
    else if (typeof j.error_description === 'string') s = j.error_description;
  } catch {
    /* use s as-is */
  }
  if (/provider is not enabled|Unsupported provider|validation_failed/i.test(s)) {
    return 'Google sign-in is not set up in Supabase yet. Open your project → Authentication → Providers → Google → enable it and paste your Google OAuth Client ID and Client Secret.';
  }
  return s || 'Sign-in failed. Try again or use another method.';
}

// Auth Callback Handler for Supabase
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setDbUser, markBootstrapComplete } = useAuth();

  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, '') || '';
    const hashParams = new URLSearchParams(hash);
    const q = new URLSearchParams(window.location.search);
    const err =
      hashParams.get('error') ||
      hashParams.get('error_code') ||
      q.get('error') ||
      q.get('error_code');
    const desc =
      hashParams.get('error_description') ||
      hashParams.get('msg') ||
      q.get('error_description') ||
      q.get('msg');
    if (err || desc) {
      toast.error(oauthErrorMessage(desc || err));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      const maxAttempts = 80;
      let session = null;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelled) return;
        const { data: { session: s } } = await supabase.auth.getSession();
        if (s?.user) {
          session = s;
          break;
        }
        await new Promise((r) => setTimeout(r, 100));
      }

      if (cancelled) return;

      if (!session?.user) {
        toast.error('Could not complete sign-in. Please try again.');
        navigate('/login', { replace: true });
        return;
      }

      const sync = await syncAppUserFromBackend(session.access_token);
      let profile = sync.user;
      if (!profile) {
        if (sync.error) {
          toast.error(sync.error, { duration: 10000 });
        }
        const { data: row } = await supabase
          .from('users')
          .select('*')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();
        profile = row ?? null;
      }

      if (cancelled) return;

      profile = await mergeCurrentChapterIntoProfile(profile);
      setDbUser(profile ?? null);
      markBootstrapComplete();
      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      if (profile?.onboarding_completed) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, setDbUser, markBootstrapComplete]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-secondary font-mono text-sm">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading, dbUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const path = location.pathname;
  const allowIncomplete =
    path === '/onboarding' || path === '/settings' || path.startsWith('/settings/');
  if (!dbUser?.onboarding_completed && !allowIncomplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Landing Page
const Landing = () => {
  const { user, loading } = useAuth();

  // Wait for session resolution before redirecting; avoids / → /dashboard while global loading is still true
  // (localhost often has a cached Supabase session; LAN IP origin does not — different localStorage).
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary font-mono text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        toast.error(oauthErrorMessage(error.message || error));
        return;
      }
    } catch (error) {
      console.error('Error signing in:', error);
      toast.error(oauthErrorMessage(error));
    }
  };

  return (
    <div className="min-h-screen bg-bg-base relative overflow-hidden">
      {/* Decorative background — layered radials for depth */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-gold-light/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-sage-light/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.025]">
          <svg width="100%" height="100%" className="text-gold">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="0.8" fill="currentColor"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <DaypopBrand logoSize={40} />
            <button
              onClick={handleLogin}
              className="px-6 py-2.5 text-sm font-medium bg-bg-surface/80 backdrop-blur-sm border border-border text-text-primary hover:bg-gold hover:text-white hover:border-gold transition-all duration-300 rounded-xl shadow-sm"
              data-testid="header-login-button"
            >
              Sign In
            </button>
          </div>
        </header>

        {/* Hero */}
        <main className="max-w-6xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center space-y-8">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-bg-surface/80 backdrop-blur-sm rounded-full border border-border shadow-sm animate-fade-up">
              <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
              <p className="font-mono text-xs uppercase tracking-widest text-gold-muted font-semibold">
                All paths welcome
              </p>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] font-bold text-text-primary leading-[1.05] tracking-tight animate-fade-up" style={{ animationDelay: '100ms' }}>
              You move fast.
              <br />
              <span className="text-gradient">Don&apos;t lose the proof.</span>
            </h1>

            <p className="text-lg lg:text-xl text-text-secondary max-w-2xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '200ms' }}>
              A private place to log what actually happened — wins, rough days, people, money, and decisions.
              No feed, no audience. Just a clear record of how far you&apos;ve come.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6 animate-fade-up" style={{ animationDelay: '300ms' }}>
              <button
                onClick={handleLogin}
                className="group px-10 py-4 bg-gold hover:bg-gold-muted text-white font-semibold rounded-2xl transition-all duration-300 hover:shadow-lg shadow-md text-base"
                data-testid="hero-start-button"
              >
                Start free — your story stays yours
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform duration-200">&rarr;</span>
              </button>
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-32 stagger-children">
            <div className="bg-bg-surface/80 backdrop-blur-sm rounded-2xl p-8 border border-border card-hover group">
              <div className="w-12 h-12 bg-gold-light rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                <svg className="w-6 h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-text-primary mb-2">Log Everything</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                Wins, setbacks, meetings, ideas, notes — your days, captured honestly.
              </p>
            </div>

            <div className="bg-bg-surface/80 backdrop-blur-sm rounded-2xl p-8 border border-border card-hover group">
              <div className="w-12 h-12 bg-amber-light rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                <svg className="w-6 h-6 text-amber" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-text-primary mb-2">Never Forget</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                Turn experience into lessons you can find again — not scattered across apps and chats.
              </p>
            </div>

            <div className="bg-bg-surface/80 backdrop-blur-sm rounded-2xl p-8 border border-border card-hover group">
              <div className="w-12 h-12 bg-sage-light rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
                <svg className="w-6 h-6 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-bold text-text-primary mb-2">See the arc</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                Goals, chapters, and lessons in one calm view — so the big picture doesn&apos;t slip away.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-border/50 py-10 mt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <DaypopLogo size={28} className="opacity-60" />
              <span className="text-sm text-text-tertiary">Made with care</span>
            </div>
            <div className="flex gap-8 text-sm text-text-tertiary">
              <Link to="/about" className="hover:text-text-primary transition-colors duration-200">About</Link>
              <Link to="/privacy" className="hover:text-text-primary transition-colors duration-200">Privacy</Link>
              <Link to="/terms" className="hover:text-text-primary transition-colors duration-200">Terms</Link>
              <Link to="/contact" className="hover:text-text-primary transition-colors duration-200">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

// Login Page — same Supabase Google OAuth as landing (no Emergent auth host)
const Login = () => {
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        toast.error(oauthErrorMessage(error.message || error));
        return;
      }
    } catch (error) {
      console.error('Error signing in:', error);
      toast.error(oauthErrorMessage(error));
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg-base px-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-gold-light/20 via-transparent to-transparent rounded-full blur-3xl" />
      <div className="relative z-10 text-center max-w-sm space-y-8 animate-fade-in-scale">
        <div className="flex justify-center">
          <DaypopBrand logoSize={48} />
        </div>
        <div className="space-y-2">
          <h1 className="font-display text-3xl font-bold text-text-primary">Welcome back</h1>
          <p className="text-text-secondary text-sm">Continue with your Google account.</p>
        </div>
        <button
          type="button"
          onClick={handleLogin}
          className="w-full px-6 py-3.5 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
        >
          Continue with Google
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-sm text-text-tertiary hover:text-text-primary transition-colors duration-200"
        >
          &larr; Back to home
        </button>
      </div>
    </div>
  );
};

// App Layout with Sidebar
const AppLayout = ({ children }) => {
  const { user, dbUser } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    window.location.href = '/';
  };
  
  return (
    <div className="flex min-h-screen bg-bg-base">
      <Sidebar user={user} dbUser={dbUser} />
      <div className="flex-1 ml-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-bg-base/80 backdrop-blur-lg border-b border-border/50 px-6 py-3 flex items-center justify-end">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-text-tertiary hover:text-error rounded-lg hover:bg-error-light/50 transition-all duration-200"
            data-testid="logout-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

function formatDashboardEchoDate(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Simple placeholder pages
const Dashboard = () => {
  const { user, dbUser } = useAuth();
  const displayName = getProfileDisplayName(user, dbUser);
  const chapterTitle = dbUser?.current_chapter?.title;
  const navigate = useNavigate();
  const [stats, setStats] = useState({ entries: 0, goals: 0, contacts: 0 });
  const [dailyEcho, setDailyEcho] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      const data = await apiFetch('/api/dashboard/stats');
      setStats({
        entries: data.total_entries ?? 0,
        goals: data.active_goals ?? 0,
        contacts: data.total_contacts ?? 0,
      });
      setDailyEcho(data.daily_echo && data.daily_echo.lesson_text ? data.daily_echo : null);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewEntry = (entryType) => {
    navigate('/log/new', { state: { entryType } });
  };
  
  const entryTypes = [
    { type: 'win', label: 'Win', emoji: '🏆', bgColor: 'bg-teal-light/60', borderColor: 'border-teal/30', hoverBg: 'hover:bg-teal-light' },
    { type: 'setback', label: 'Setback', emoji: '⚡', bgColor: 'bg-amber-light/60', borderColor: 'border-amber/30', hoverBg: 'hover:bg-amber-light' },
    { type: 'meeting', label: 'Meeting', emoji: '🤝', bgColor: 'bg-teal-light/40', borderColor: 'border-teal/20', hoverBg: 'hover:bg-teal-light/70' },
    { type: 'idea', label: 'Idea', emoji: '💡', bgColor: 'bg-lavender-light/60', borderColor: 'border-lavender/30', hoverBg: 'hover:bg-lavender-light' },
    { type: 'milestone', label: 'Milestone', emoji: '🎯', bgColor: 'bg-sage-light/60', borderColor: 'border-sage/30', hoverBg: 'hover:bg-sage-light' },
    { type: 'neutral', label: 'Note', emoji: '📝', bgColor: 'bg-bg-surface-alt/60', borderColor: 'border-text-tertiary/20', hoverBg: 'hover:bg-bg-surface-alt' }
  ];
  
  return (
    <AppLayout>
      <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="dashboard">
        <div className="max-w-7xl mx-auto animate-fade-up">
          {/* Greeting */}
          <div className="mb-10">
            <p className="text-sm font-mono text-text-tertiary uppercase tracking-wider mb-2">
              {chapterTitle ? `Chapter: ${chapterTitle}` : 'Your dashboard'}
            </p>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-text-primary">
              Welcome back, <span className="text-gradient">{displayName}</span>
            </h1>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Today's prompt */}
              <div className="bg-bg-surface rounded-2xl p-8 border border-border shadow-sm card-hover">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-text-primary mb-1">How did today go?</h2>
                    <p className="text-sm text-text-secondary">Pick a type to log an entry</p>
                  </div>
                  <div className="w-10 h-10 bg-gold-light rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {entryTypes.map(item => (
                    <button
                      key={item.type}
                      onClick={() => handleNewEntry(item.type)}
                      className={`p-4 rounded-xl border ${item.borderColor} ${item.bgColor} ${item.hoverBg} transition-all duration-200 cursor-pointer hover:scale-[1.03] hover:shadow-sm active:scale-[0.97]`}
                      data-testid={`entry-type-${item.type}`}
                    >
                      <div className="text-2xl mb-1.5">{item.emoji}</div>
                      <div className="text-sm font-medium text-text-primary">{item.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-bg-surface rounded-xl p-5 border border-border card-hover">
                  <div className="text-2xl font-bold text-gold mb-0.5 font-mono">
                    {loading ? '—' : stats.entries}
                  </div>
                  <div className="text-xs text-text-tertiary uppercase tracking-wide">Entries</div>
                </div>
                <div className="bg-bg-surface rounded-xl p-5 border border-border card-hover">
                  <div className="text-2xl font-bold text-sage mb-0.5 font-mono">
                    {loading ? '—' : stats.goals}
                  </div>
                  <div className="text-xs text-text-tertiary uppercase tracking-wide">Goals</div>
                </div>
                <div className="bg-bg-surface rounded-xl p-5 border border-border card-hover">
                  <div className="text-2xl font-bold text-teal mb-0.5 font-mono">
                    {loading ? '—' : stats.contacts}
                  </div>
                  <div className="text-xs text-text-tertiary uppercase tracking-wide">Contacts</div>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-6">
              {/* Daily Echo */}
              <div className="bg-bg-surface rounded-2xl p-6 border border-border relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-light/30 to-transparent rounded-full -mr-8 -mt-8" />
                <div className="relative">
                <div className="font-mono text-xs uppercase tracking-wider text-amber-muted mb-3">Today&apos;s Echo</div>
                <p className="font-display text-lg italic text-text-primary leading-relaxed">
                  {dailyEcho?.lesson_text ? (
                    <>&ldquo;{dailyEcho.lesson_text}&rdquo;</>
                  ) : (
                    <>
                      &ldquo;Log setbacks and wins as entries, then capture lessons on the Lessons page —
                      your echoes will surface here.&rdquo;
                    </>
                  )}
                </p>
                {dailyEcho ? (
                  <p className="text-xs text-text-tertiary mt-3">
                    From your lessons
                    {dailyEcho.category ? (
                      <span>
                        {' '}
                        · <span className="capitalize">{dailyEcho.category}</span>
                      </span>
                    ) : null}
                    {dailyEcho.date_learned ? (
                      <span> · learned {formatDashboardEchoDate(dailyEcho.date_learned)}</span>
                    ) : null}
                  </p>
                ) : null}
                </div>
              </div>

              {/* Plan badge */}
              <div className="bg-bg-surface rounded-2xl p-6 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-lg font-bold text-text-primary">Free plan</div>
                  <div className="px-2.5 py-1 bg-gold-light text-gold text-[10px] font-bold rounded-full uppercase tracking-wider">Active</div>
                </div>
                <p className="text-sm text-text-secondary mb-4 leading-relaxed">
                  Unlimited entries, goals, and core features.
                </p>
                <button
                  type="button"
                  className="w-full py-2.5 bg-gold hover:bg-gold-muted text-white font-medium rounded-xl transition-all duration-200 text-sm shadow-sm hover:shadow-md"
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

const Onboarding = () => {
  const navigate = useNavigate();
  const { refreshUser, setDbUser, dbUser } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    journey_type: '',
    chapter_name: '',
    goal_title: '',
    goal_why: '',
    goal_category: 'revenue',
    goal_target_date: '',
    contact_name: '',
    contact_relevance: '',
    contact_discussion: '',
    reminder_time: '20:00'
  });

  useEffect(() => {
    if (dbUser?.onboarding_completed) {
      navigate('/dashboard', { replace: true });
    }
  }, [dbUser?.onboarding_completed, navigate]);

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      const result = await apiFetch('/api/onboarding/complete', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (result.user) {
        setDbUser(result.user);
      } else {
        await refreshUser();
      }
      if (result.skipped) {
        toast.success('You’re already set up — welcome back.');
      } else {
        toast.success('Welcome to Daypop!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error?.message || 'Could not finish onboarding. Is the API running on port 8001?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-8 flex items-center justify-center relative overflow-hidden" data-testid="onboarding">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-gold-light/20 via-transparent to-transparent rounded-full blur-3xl" />
      <div className="relative z-10 max-w-2xl w-full">
        <p className="text-center text-sm text-text-tertiary mb-6">
          <Link to="/settings" className="text-gold hover:underline">
            Stuck mid-setup? Edit your journey in Settings
          </Link>
        </p>
        {/* Progress */}
        <div className="flex justify-center gap-2 mb-12">
          {[1,2,3,4,5].map(i => (
            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
              i === step ? 'bg-gold w-8' : i < step ? 'bg-gold-muted w-4' : 'bg-text-tertiary/30 w-4'
            }`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold text-text-primary mb-2">What best fits this chapter?</h1>
              <p className="text-text-secondary">Choose the closest match — nothing here is permanent.</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'startup', label: 'Business or venture', icon: '🚀' },
                { value: 'personal_brand', label: 'Life, craft, or identity', icon: '👤' },
                { value: 'freelance', label: 'Career & clients', icon: '💼' },
                { value: 'other', label: 'Something else', icon: '✨' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setData({...data, journey_type: option.value})}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    data.journey_type === option.value
                      ? 'border-gold bg-gold/5'
                      : 'border-border hover:border-border-strong bg-bg-surface'
                  }`}
                  data-testid={`journey-type-${option.value}`}
                >
                  <div className="text-3xl mb-2">{option.icon}</div>
                  <div className="font-medium text-text-primary">{option.label}</div>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setStep(2)}
              disabled={!data.journey_type}
              className="w-full py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary disabled:cursor-not-allowed text-bg-base font-medium rounded-lg transition-colors"
              data-testid="continue-button"
            >
              Continue →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold text-text-primary mb-2">Every journey has a beginning</h1>
              <p className="text-text-secondary">What do you call this phase of your life right now?</p>
            </div>
            
            <div>
              <label className="block text-text-secondary text-sm mb-2">Chapter Name</label>
              <input
                type="text"
                value={data.chapter_name}
                onChange={(e) => setData({...data, chapter_name: e.target.value})}
                placeholder="e.g. New role, Family season, The quiet reset..."
                className="w-full px-4 py-3 bg-bg-input border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                data-testid="chapter-name-input"
              />
              <p className="text-text-tertiary text-xs mt-2 italic">Don't overthink it. You can rename this anytime.</p>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-border text-text-secondary rounded-lg hover:border-border-strong transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!data.chapter_name}
                className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary disabled:cursor-not-allowed text-bg-base font-medium rounded-lg transition-colors"
                data-testid="continue-button"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold text-text-primary mb-2">What matters most right now?</h1>
              <p className="text-text-secondary">Name one focus for the next stretch — big or small.</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Goal Title</label>
                <input
                  type="text"
                  value={data.goal_title}
                  onChange={(e) => setData({...data, goal_title: e.target.value})}
                  placeholder="e.g. Finish the course, Show up 3× a week, Close the loop with…"
                  className="w-full px-4 py-3 bg-bg-input border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  data-testid="goal-title-input"
                />
              </div>
              
              <div>
                <label className="block text-text-secondary text-sm mb-2">Why does this matter?</label>
                <textarea
                  value={data.goal_why}
                  onChange={(e) => setData({...data, goal_why: e.target.value})}
                  placeholder="Because..."
                  rows={3}
                  className="w-full px-4 py-3 bg-bg-input border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none resize-none"
                  data-testid="goal-why-input"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <button onClick={() => setStep(2)} className="px-6 py-3 border border-border text-text-secondary rounded-lg hover:border-border-strong transition-colors">← Back</button>
              <button
                onClick={() => setStep(4)}
                disabled={!data.goal_title || !data.goal_why}
                className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary disabled:cursor-not-allowed text-bg-base font-medium rounded-lg transition-colors"
                data-testid="continue-button"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold text-text-primary mb-2">Who is someone important to your journey right now?</h1>
              <p className="text-text-secondary">Add them to your Orbit.</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Name (optional)</label>
                <input
                  type="text"
                  value={data.contact_name}
                  onChange={(e) => setData({...data, contact_name: e.target.value})}
                  placeholder="Their name"
                  className="w-full px-4 py-3 bg-bg-input border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  data-testid="contact-name-input"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <button onClick={() => setStep(3)} className="px-6 py-3 border border-border text-text-secondary rounded-lg hover:border-border-strong transition-colors">← Back</button>
              <button
                onClick={() => setStep(5)}
                className="flex-1 py-3 bg-gold hover:bg-gold-muted text-bg-base font-medium rounded-lg transition-colors"
                data-testid="continue-button"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold text-text-primary mb-2">When should we remind you to log your day?</h1>
              <p className="text-text-secondary">Building the habit is everything.</p>
            </div>
            
            <div>
              <label className="block text-text-secondary text-sm mb-2">Daily reminder time</label>
              <input
                type="time"
                value={data.reminder_time}
                onChange={(e) => setData({...data, reminder_time: e.target.value})}
                className="w-full px-4 py-3 bg-bg-input border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                data-testid="reminder-time-input"
              />
            </div>
            
            <div className="flex gap-4">
              <button onClick={() => setStep(4)} className="px-6 py-3 border border-border text-text-secondary rounded-lg hover:border-border-strong transition-colors">← Back</button>
              <button
                type="button"
                onClick={handleComplete}
                disabled={submitting}
                className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:opacity-60 disabled:cursor-not-allowed text-bg-base font-medium rounded-lg transition-colors"
                data-testid="complete-button"
              >
                {submitting ? 'Saving…' : 'Start My Journey →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Only hijack root when legacy OAuth lands on "/" with a session fragment.
  // A global check broke /dashboard (etc.): hash lingered → endless AuthCallback "Authenticating…".
  if (
    location.pathname === '/' &&
    location.hash?.includes('session_id=')
  ) {
    return <AuthCallback />;
  }

  return (
    <>
      <SessionTimeoutWatcher />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/log" element={<ProtectedRoute><AppLayout><LogPage /></AppLayout></ProtectedRoute>} />
        <Route path="/log/edit/:entryId" element={<ProtectedRoute><AppLayout><NewEntryPage /></AppLayout></ProtectedRoute>} />
        <Route path="/log/new" element={<ProtectedRoute><AppLayout><NewEntryPage /></AppLayout></ProtectedRoute>} />
        <Route path="/goals" element={<ProtectedRoute><AppLayout><GoalsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/orbit" element={<ProtectedRoute><AppLayout><OrbitPage /></AppLayout></ProtectedRoute>} />
        <Route path="/lessons" element={<ProtectedRoute><AppLayout><LessonsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/ideas" element={<ProtectedRoute><AppLayout><IdeasPage /></AppLayout></ProtectedRoute>} />
        <Route path="/decisions" element={<ProtectedRoute><AppLayout><DecisionsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/journey" element={<ProtectedRoute><AppLayout><JourneyPage /></AppLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
        <Route
          path="/u/:username"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PublicProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />

        {/* Public Showcase */}
        <Route path="/u/:username" element={<ShowcasePage />} />

        {/* Legal & Info Pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/contact" element={<ContactPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
